import { useEffect, useRef, useState } from 'react';
import {
  Link,
  NavLink,
  Outlet,
  ScrollRestoration,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import { ConnectionBar } from './ConnectionBar';
import { ExternalLinkIcon } from './ExternalLinkIcon';
import { Logo } from './Logo';
import { ThemeToggle } from './ThemeToggle';
import { useTheme } from '../lib/theme';
import { useConnection } from '../lib/connection';
import { useAuth } from '../lib/auth';
import { SHOW_AUTH_PAGES } from '../lib/flags';
import { scrollKey, useRestoreAsPageGrows } from '../lib/scroll';
import { DOCS_URL } from '../lib/chains';
import {
  BLOCKCHAIN_EXPLORER_URL,
  WILLOW_DISCORD,
  WILLOW_GITHUB,
  WILLOW_HOME,
  WILLOW_X,
} from '../lib/links';

/** The RPC account sections: the header nav once signed in. */
const ACCOUNT_SECTIONS = [
  { to: '/account', label: 'Overview', testid: 'account-nav-overview' },
  { to: '/keys', label: 'API keys', testid: 'account-nav-keys' },
  { to: '/usage', label: 'Usage', testid: 'account-nav-usage' },
  { to: '/billing', label: 'Billing', testid: 'account-nav-billing' },
];

/** Network explorer routes, kept out of the nav; only they get the node connection menu. */
const EXPLORER_PATHS = ['/home', '/network', '/subgroves'];

/** willow.tech's footer links, as absolute URLs. */
const SITE_LINKS = [
  { href: `${WILLOW_HOME}/blog`, label: 'Blog' },
  { href: `${WILLOW_HOME}/team`, label: 'Team' },
  { href: `${WILLOW_HOME}/roadmap`, label: 'Roadmap' },
  { href: `${WILLOW_HOME}/explained`, label: 'Plain English' },
  { href: `${WILLOW_HOME}/faq`, label: 'FAQ' },
  { href: `${WILLOW_HOME}/brand`, label: 'Brand' },
];

/** The signed-in email, opening a small menu with sign-out (willow.tech's dropdown style). */
function AccountMenu({ email }: { email: string }) {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Close on an outside click or Escape.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  async function onSignOut() {
    setOpen(false);
    await logout();
    navigate('/', { replace: true });
  }

  return (
    <div className="nav-menu" ref={ref}>
      <button
        type="button"
        className="nav-account"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        data-testid="auth-account"
      >
        <span className="nav-account-email" data-testid="account-email">
          {email}
        </span>
        <svg viewBox="0 0 12 12" width="10" height="10" aria-hidden="true">
          <path d="M2.5 4.5 6 8l3.5-3.5" fill="none" stroke="currentColor" strokeWidth="1.5" />
        </svg>
      </button>
      {open && (
        <div className="nav-menu-list" role="menu">
          <Link to="/account" role="menuitem" className="nav-menu-item" onClick={() => setOpen(false)}>
            Account
            <span>{email}</span>
          </Link>
          <button type="button" role="menuitem" className="nav-menu-item" onClick={onSignOut} data-testid="sign-out">
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}

export function Layout() {
  const conn = useConnection();
  const [theme, , toggleTheme] = useTheme();
  const { user, loading: authLoading } = useAuth();
  const { pathname } = useLocation();
  useRestoreAsPageGrows();
  const onExplorer = EXPLORER_PATHS.some((p) => pathname === p || pathname.startsWith(`${p}/`));
  // Sign-in still being checked on an account page: its section nav is drawn
  // already, and the footer waits; at the bottom of the short loading page it
  // would jump down once the page fills.
  const accountPending =
    SHOW_AUTH_PAGES && authLoading && ACCOUNT_SECTIONS.some((s) => s.to === pathname);
  const signedIn = SHOW_AUTH_PAGES && (!!user || accountPending);
  const linkClass = ({ isActive }: { isActive: boolean }) =>
    `nav-link${isActive ? ' is-active' : ''}`;

  return (
    <div className="app">
      {/* New navigations land at the top; Back and Forward restore the position.
          A navigation inside a page (a tab, a filter) passes `preventScrollReset`
          to stay where it is. */}
      <ScrollRestoration getKey={scrollKey} />
      <header className="nav">
        <div className="container nav-inner">
          <Link to="/" className="nav-brand" aria-label="Willow Dashboard home">
            <Logo size={26} />
            <span className="nav-product">Dashboard</span>
          </Link>
          <nav className="nav-links" aria-label="Primary">
            {signedIn &&
              ACCOUNT_SECTIONS.map((s) => (
                <NavLink key={s.to} to={s.to} end className={linkClass} data-testid={s.testid}>
                  {s.label}
                </NavLink>
              ))}
            {!signedIn && (
              <NavLink to="/pricing" className={linkClass}>
                Pricing
              </NavLink>
            )}
            {SHOW_AUTH_PAGES && !authLoading && !user && (
              <NavLink to="/login" className={(a) => `${linkClass(a)} nav-link--phone`}>
                Sign in
              </NavLink>
            )}
            <a href={DOCS_URL} className="nav-link" target="_blank" rel="noreferrer">
              Docs
              <ExternalLinkIcon />
            </a>
          </nav>
          <div className="nav-actions">
            {onExplorer && (
              <ConnectionBar
                apiUrl={conn.apiUrl}
                indexerUrl={conn.indexerUrl}
                consensusUrl={conn.consensusUrl}
                onApiUrlChange={conn.setApiUrl}
                onIndexerUrlChange={conn.setIndexerUrl}
                onConsensusUrlChange={conn.setConsensusUrl}
              />
            )}
            {SHOW_AUTH_PAGES && !authLoading && (
              user ? (
                <AccountMenu email={user.email ?? user.wallet_address ?? 'Account'} />
              ) : (
                <>
                  <Link to="/login" className="nav-signin" data-testid="auth-signin">
                    Sign in
                  </Link>
                  <Link to="/signup" className="nav-cta">
                    Get an API key
                  </Link>
                </>
              )
            )}
            <ThemeToggle theme={theme} onToggle={toggleTheme} />
          </div>
        </div>
      </header>

      <main className="app-main">
        <Outlet />
      </main>

      <footer className="footer" hidden={accountPending}>
        <div className="container footer-inner">
          <a href={WILLOW_HOME} className="footer-brand" aria-label="willow.tech">
            <Logo size={20} />
            <span className="footer-copy">© {new Date().getFullYear()}</span>
          </a>
          <nav className="footer-links" aria-label="Footer">
            <a href={DOCS_URL} target="_blank" rel="noreferrer">
              Docs
              <ExternalLinkIcon />
            </a>
            <Link to="/pricing">Pricing</Link>
            <a href={BLOCKCHAIN_EXPLORER_URL} target="_blank" rel="noreferrer">
              Explorer
              <ExternalLinkIcon />
            </a>
            {SITE_LINKS.map((l) => (
              <a key={l.label} href={l.href}>
                {l.label}
              </a>
            ))}
            <a href={WILLOW_GITHUB} target="_blank" rel="noreferrer">
              GitHub
              <ExternalLinkIcon />
            </a>
            <a href={WILLOW_X} target="_blank" rel="noreferrer">
              X
              <ExternalLinkIcon />
            </a>
            <a href={WILLOW_DISCORD} target="_blank" rel="noreferrer">
              Discord
              <ExternalLinkIcon />
            </a>
            {/* Together, so Terms never wraps onto a line alone. */}
            <span className="footer-legal">
              <a href={`${WILLOW_HOME}/privacy`} className="footer-link--quiet">
                Privacy
              </a>
              <a href={`${WILLOW_HOME}/terms`} className="footer-link--quiet">
                Terms
              </a>
            </span>
          </nav>
        </div>
      </footer>
    </div>
  );
}
